import React, { useCallback, useEffect, useMemo } from "react";
import { PORT_VIEW, PortHookInterface, editPortsHook, editPortsInterface, loggerListInterface } from "./portDetailsHook";
import ManageBandwidthService from "../../services/manageBandwidth.service";
import { throwGenericToasterError, toasterError, toasterSuccess } from "../../utils/methods";
import { rollServiceType } from "./useRollService";
import RollTrafficService from "../../services/rollTraffic.service";
import { usePutUpForSaleBandwidth } from "../ManageBandwidth/BandwidthTable/PutUpForSale/usePutupForSaleBandwidth";

interface listBandwidthPort {
  bandwidth_description: string;
  bandwidth_detail_id: number;
  service_description: string;
  on_contract_price: number;
  on_demand_price: number;
  near_requested_verification: boolean;
  far_requested_verification: boolean;
  nearend_verified: boolean;
  farend_verified: boolean;
  is_verified: boolean;
  nne_name: string;
  fne_name: string;
}
export interface BandwidthHookInterface {
  name: string;
  description: string;
  logs: loggerListInterface[];
  setName: React.Dispatch<React.SetStateAction<string>>;
  setDescription: React.Dispatch<React.SetStateAction<string>>;
  submit: () => void;
  reset: () => void;
  loadingSubmit: boolean;
  isNameOrDescriptionChanged: boolean;
  cancelSubscription: (_: number | null) => void;
  cancelLoading: boolean;
  rollService: rollServiceType;
  listService: {
    hook: ReturnType<typeof usePutUpForSaleBandwidth>;
    data: listBandwidthPort;
  };
  deleteBandwidth: {
    do: (_: number | undefined) => void;
    loading: boolean;
  };
}

export function isBandwidthHook(obj: any): obj is BandwidthHookInterface {
  const isHook =
    obj instanceof Object &&
    typeof obj.name === "string" &&
    typeof obj.description === "string" &&
    Array.isArray(obj.logs) &&
    typeof obj.setName === "function" &&
    typeof obj.setDescription === "function" &&
    typeof obj.submit === "function" &&
    typeof obj.reset === "function" &&
    typeof obj.loadingSubmit === "boolean" &&
    typeof obj.isNameOrDescriptionChanged === "boolean" &&
    typeof obj.cancelSubscription === "function" &&
    typeof obj.cancelLoading === "boolean";
  return isHook;
}

export function useBandwidthHook(
  leftPortProps: PortHookInterface,
  rightPortProps: PortHookInterface,
  isMounted: boolean,
  setChangedTrue: () => void,
  refreshIndex: () => void
) {
  const { portDetails: leftPortDetails, logs: leftLogs, refetchPort: refetchLeft } = leftPortProps;
  const { portDetails: rightPortDetails, refetchPort: refetchRight } = rightPortProps;
  const refetch = useCallback(() => {
    refetchLeft();
    refetchRight();
    setChangedTrue();
  }, [refetchLeft, refetchRight, setChangedTrue]);

  const defaults = useMemo(() => {
    return { name: leftPortDetails?.bandwidth_name || "", description: leftPortDetails?.bandwidth_description || "" };
  }, [leftPortDetails]);
  const [bandwidthName, setBandwidthName] = React.useState(defaults.name);
  const [bandwidthDescription, setBandwidthDescription] = React.useState(defaults.description);
  const [editLoading, setEditLoading] = React.useState(false);
  const [rollTrafficLoading, setRollTrafficLoading] = React.useState(false);
  const [cancelLoading, setCancelLoading] = React.useState(false);
  const [deleteBandwidthLoading, setDeleteBandwidthLoading] = React.useState(false);
  const [listingLoading, setListingLoading] = React.useState(false);

  const reset = React.useCallback(() => {
    setBandwidthDescription(defaults.description);
    setBandwidthName(defaults.name);
  }, [leftPortDetails, rightPortDetails]);

  const isNameOrDescriptionChanged = React.useMemo(() => {
    return defaults.description !== bandwidthDescription || defaults.name !== bandwidthName;
  }, [defaults, bandwidthDescription, bandwidthName]);

  useEffect(() => {
    reset();
  }, [leftPortDetails, rightPortDetails]);

  const submit = React.useCallback(() => {
    if (!leftPortDetails || !leftPortDetails.bandwidth_id) {
      throwGenericToasterError();
      return;
    }
    setEditLoading(true);
    ManageBandwidthService.editBandwidth({
      name: bandwidthName || "",
      description: bandwidthDescription || "",
      bandwidthDetailsId: leftPortDetails.bandwidth_id,
    })
      .then(() => {
        if (!isMounted) return;
        refetch();
        toasterSuccess("Bandwidth Edit Successful.");
        setEditLoading(false);
      })
      .catch((e) => {
        if (!isMounted) return;
        toasterError("Bandwidth Could Not Be Edited");
        setEditLoading(false);
      });
  }, [bandwidthDescription, bandwidthName]);

  const cancelSubscription = React.useCallback(
    (bandwidthDetailId: number | undefined) => {
      if (!bandwidthDetailId) {
        throwGenericToasterError();
        return;
      }
      setCancelLoading(true);
      ManageBandwidthService.cancelSubscription({
        purchaseId: "ignoring",
        bandwidthDetailId,
      })
        .then(() => {
          if (!isMounted) return;
          toasterSuccess("Success");
          setCancelLoading(false);
          refreshIndex();
        })
        .catch((err) => {
          if (!isMounted) return;
          setCancelLoading(false);
          throwGenericToasterError(err);
        });
    },
    [isMounted]
  );

  const deleteBandwidth = React.useCallback(
    (bandwidthDetailId: number | null) => {
      if (!bandwidthDetailId) {
        throwGenericToasterError();
        return;
      }
      setDeleteBandwidthLoading(true);
      ManageBandwidthService.deleteOffNetBandwidth({
        bandwidthId: bandwidthDetailId.toString(),
      })
        .then(() => {
          if (!isMounted) return;
          toasterSuccess("Success");
          setDeleteBandwidthLoading(false);
          refreshIndex();
        })
        .catch((err) => {
          if (!isMounted) return;
          setDeleteBandwidthLoading(false);
          throwGenericToasterError(err);
        });
    },
    [isMounted]
  );

  const rollService = React.useCallback(
    (bandwidthDetailId: number | null) => {
      if (!bandwidthDetailId || !leftPortDetails?.bandwidth_id) {
        throwGenericToasterError();
        return;
      }
      setRollTrafficLoading(true);
      RollTrafficService.rollTrafficBandwidth({
        bandwidthIdOne: leftPortDetails?.bandwidth_id,
        bandwidthIdTwo: bandwidthDetailId,
      })
        .then(() => {
          if (!isMounted) return;
          toasterSuccess("Success");
          setRollTrafficLoading(false);
          refetch();
        })
        .catch((err) => {
          if (!isMounted) return;
          setRollTrafficLoading(false);
          throwGenericToasterError(err);
        });
    },
    [leftPortDetails, isMounted]
  );

  const listServiceHook = usePutUpForSaleBandwidth(isMounted, refetch, listingLoading, setListingLoading, true);
  const listServiceData = useMemo(() => {
    if (!leftPortDetails || !rightPortDetails) return undefined;
    return {
      bandwidth_description: leftPortDetails.bandwidth_description,
      bandwidth_detail_id: leftPortDetails.bandwidth_id,
      service_description: leftPortDetails.service_description,
      on_contract_price: leftPortDetails.on_contract_price,
      on_demand_price: leftPortDetails.on_demand_price,
      near_requested_verification: leftPortDetails.requested_verification,
      far_requested_verification: rightPortDetails.requested_verification,
      nearend_verified: leftPortDetails.verified,
      farend_verified: rightPortDetails.verified,
      is_verified: leftPortDetails.bandwidth_verified,
      nne_name: leftPortDetails.ne_name,
      fne_name: rightPortDetails.ne_name,
    };
  }, [leftPortDetails, rightPortDetails]) as listBandwidthPort;

  const bandwidthHook = React.useMemo(() => {
    return {
      name: bandwidthName,
      description: bandwidthDescription,
      logs: leftLogs,
      setName: setBandwidthName,
      setDescription: setBandwidthDescription,
      submit,
      reset,
      isNameOrDescriptionChanged,
      loadingSubmit: editLoading,
      cancelSubscription,
      cancelLoading,
      rollService: {
        loading: rollTrafficLoading,
        rollService,
      },
      listService: {
        hook: listServiceHook,
        data: listServiceData,
      },
      deleteBandwidth: {
        do: deleteBandwidth,
        loading: deleteBandwidthLoading,
      },
    };
  }, [
    bandwidthName,
    bandwidthDescription,
    leftLogs,
    isNameOrDescriptionChanged,
    listServiceHook,
    listServiceData,
    deleteBandwidthLoading,
    rollTrafficLoading,
    cancelLoading,
    editLoading,
  ]) as BandwidthHookInterface;
  if (!(leftPortDetails?.bandwidth_id && leftPortDetails?.bandwidth_id === rightPortDetails?.bandwidth_id)) return false;
  return bandwidthHook;
}
